import { Link } from 'react-router-dom'
import { CheckCircle2, Circle } from 'lucide-react'
import { useProfileChecklist } from '@/hooks/useVendors'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { ProfileChecklistDto } from '@lib/dto/vendor-profile.dto'

type Step = ProfileChecklistDto['steps'][number]

/** Where a step is done: its own card on the Profile page, the furniture catalog, or Settings. */
function stepHref(step: Step): string {
  switch (step.place) {
    case 'furniture':
      return '/vendor/furniture'
    case 'settings':
      return '/vendor'
    case 'profile':
      return `/vendor/profile#${step.id}`
    default: {
      const unexpected: never = step.place
      return unexpected
    }
  }
}

function StepRow({ step }: { step: Step }) {
  return (
    <li className="flex gap-3 py-2">
      {step.done ? (
        <CheckCircle2 aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ok-ink" />
      ) : (
        <Circle aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" />
      )}
      <div className="min-w-0">
        {step.done ? (
          <p className="text-sm text-ink-muted line-through">
            {step.label}
            <span className="sr-only"> (done)</span>
          </p>
        ) : (
          <Link to={stepHref(step)} className="text-sm font-medium text-ink underline-offset-2 hover:underline">
            {step.label}
          </Link>
        )}
        <p className="text-xs text-ink-muted">{step.why}</p>
      </div>
    </li>
  )
}

/**
 * What the company's profile still needs (docs/vendor-growth.spec.md, "First
 * sign-in"): its events, its Profile page, and the events it plans show it to
 * everyone at the company until every step is done, then it goes. There is no
 * dismissing it; finishing it is the way.
 */
export function ProfileChecklistCard({ className }: { className?: string }) {
  const checklist = useProfileChecklist()
  // Nothing while it loads or if it can't: the page around it is what was asked for.
  if (!checklist.data || checklist.data.done) return null
  const { steps } = checklist.data
  const done = steps.filter((s) => s.done).length
  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle>Finish your profile</CardTitle>
        <CardDescription>
          {String(done)} of {String(steps.length)} done
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-line">
          {steps.map((step) => (
            <StepRow key={step.id} step={step} />
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
